import {
  createMongoAbility,
  subject,
  type MongoAbility,
  type Subject as CaslSubject,
} from '@casl/ability'
import { unpackRules } from '@casl/ability/extra'

/**
 * Rehydrates the backend's serialized CASL ruleset.
 *
 * The critical property: the frontend NEVER re-implements a permission rule. The backend
 * packs `ability.rules`, this unpacks them, and both sides evaluate identical semantics.
 * Any `if (role === 'admin')` in feature code is a bug — use `can()`.
 */

export type Action =
  | 'create'
  | 'read'
  | 'update'
  | 'delete'
  | 'move'
  | 'assign'
  | 'comment'
  | 'archive'
  | 'invite'
  | 'export'
  | 'manage'

export type Subject =
  | 'Org'
  | 'Workspace'
  | 'Team'
  | 'Board'
  | 'List'
  | 'Card'
  | 'Client'
  | 'Project'
  | 'Geography'
  | 'Comment'
  | 'Label'
  | 'AttributeDef'
  | 'SavedView'
  | 'Role'
  | 'Member'
  | 'Audit'
  | 'FinanceRequest'
  | 'Crm'
  | 'Product'
  | 'Procurement'
  | 'Hr'
  | 'Report'
  | 'all'

/**
 * Subjects are CASL's own `Subject` type, not `Subject | Record<string, unknown>`.
 * CASL's version uses `any`-valued records, which is what allows a plain interface like
 * `Card` (no index signature) to be passed to `can()`. Narrowing it to `unknown` values
 * rejects every tagged entity.
 */
export type AppAbility = MongoAbility<[Action, SubjectName | CaslSubject]>

/** The string names, for `can('read', 'Board')` style checks. */
export type SubjectName = Subject

export function createAppAbility(packedRules: unknown[]): AppAbility {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return createMongoAbility(unpackRules(packedRules as any), {
    detectSubjectType: (o) => (o as { __type?: Subject }).__type ?? 'all',
  })
}

export const emptyAbility = (): AppAbility => createMongoAbility([])

/**
 * Tags a plain JSON entity with its subject type.
 *
 * CASL cannot infer a subject type from plain JSON, and an untagged object silently
 * evaluates as 'all' — which usually means "denied" and looks like a config bug. Always
 * wrap. These helpers are the only place `subject()` is called.
 */
export const asBoard = <T extends object>(b: T) => subject('Board', { ...b, __type: 'Board' })
export const asCard = <T extends object>(c: T) => subject('Card', { ...c, __type: 'Card' })
export const asClient = <T extends object>(c: T) =>
  subject('Client', { ...c, __type: 'Client' })
export const asProject = <T extends object>(p: T) =>
  subject('Project', { ...p, __type: 'Project' })
export const asGeography = <T extends object>(g: T) =>
  subject('Geography', { ...g, __type: 'Geography' })
export const asTeam = <T extends object>(t: T) => subject('Team', { ...t, __type: 'Team' })
export const asMember = <T extends object>(m: T) => subject('Member', { ...m, __type: 'Member' })
export const asComment = <T extends object>(c: T) =>
  subject('Comment', { ...c, __type: 'Comment' })
export const asView = <T extends object>(v: T) =>
  subject('SavedView', { ...v, __type: 'SavedView' })
export const asWorkspace = <T extends object>(w: T) =>
  subject('Workspace', { ...w, __type: 'Workspace' })
